import React from 'react'
import { FunnelIcon, ArrowsUpDownIcon, ViewColumnsIcon, CheckIcon, CheckCircleIcon, MagnifyingGlassIcon } from '@heroicons/react/16/solid'
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from './Dropdown'
import { cn } from '../lib/utils'

const triggerClasses =
    'inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-xs font-medium text-foreground data-[hover]:bg-accent [&>svg]:size-3.5'

/** Row for Search (left) + Filters/Sort/Columns (right) — sits inside the Card, above the table. `search` is optional so existing call sites keep their old right-only layout until migrated. */
export function ListToolbarRow({ children, search }: { children: React.ReactNode; search?: React.ReactNode }) {
    return (
        <div className={cn('mb-4 flex flex-wrap items-center gap-2', search ? 'justify-between' : 'justify-end')}>
            {search}
            <div className="flex flex-wrap items-center gap-2">{children}</div>
        </div>
    )
}

/** Left-aligned search input for the ListToolbarRow — compact, matches the Filters/Sort/Columns button scale. */
export function ListToolbarSearch({
    value,
    onChange,
    placeholder = 'Search…',
    className,
    onKeyDown,
}: {
    value: string
    onChange: (value: string) => void
    placeholder?: string
    className?: string
    onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
}) {
    return (
        <div className={cn('relative w-full max-w-xs', className)}>
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                className="w-full rounded-full bg-secondary py-1.5 pl-8 pr-3 text-xs font-medium text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
        </div>
    )
}

/** Top-right toggle for a page's existing filter panel — wire `active`/`onClick` to the page's own isFilterOpen state. */
export function FiltersToggleButton({ active, onClick }: { active: boolean; onClick: () => void }) {
    return (
        <button type="button" onClick={onClick} className={cn(triggerClasses, active && 'bg-accent')}>
            <FunnelIcon />
            Filters
        </button>
    )
}

/** Opens the standardized FiltersDialog. `count` shows how many filters are currently active. */
export function FiltersButton({ count, onClick }: { count: number; onClick: () => void }) {
    return (
        <button type="button" onClick={onClick} className={cn(triggerClasses, count > 0 && 'bg-accent')}>
            <FunnelIcon />
            Filters
            {count > 0 && (
                <span className="inline-flex size-4 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">
                    {count}
                </span>
            )}
        </button>
    )
}

/** Top-right toggle for a table's row-selection checkboxes — hidden by default, shown on click. */
export function SelectToggleButton({ active, onClick }: { active: boolean; onClick: () => void }) {
    return (
        <button type="button" onClick={onClick} className={cn(triggerClasses, active && 'bg-accent')}>
            <CheckCircleIcon />
            Select
        </button>
    )
}

export interface SortOption {
    label: string
    value: string
}

export function SortMenu({
    options,
    value,
    onChange,
}: {
    options: SortOption[]
    value: string
    onChange: (value: string) => void
}) {
    return (
        <Dropdown>
            <DropdownButton as="button" type="button" className={triggerClasses}>
                <ArrowsUpDownIcon />
                Sort
            </DropdownButton>
            <DropdownMenu anchor="bottom end">
                {options.map((opt) => (
                    <DropdownItem key={opt.value} onClick={() => onChange(opt.value)}>
                        <DropdownLabel>{opt.label}</DropdownLabel>
                        {value === opt.value && <CheckIcon data-slot="icon" className="col-start-5" />}
                    </DropdownItem>
                ))}
            </DropdownMenu>
        </Dropdown>
    )
}

export interface ColumnDef {
    key: string
    label: string
}

/** `visible[key] === false` hides a column; absent/true shows it. Pass the same map to filter TableHeader + TableCell rendering. */
export function ColumnsMenu({
    columns,
    visible,
    onToggle,
}: {
    columns: ColumnDef[]
    visible: Record<string, boolean>
    onToggle: (key: string) => void
}) {
    return (
        <Dropdown>
            <DropdownButton as="button" type="button" className={triggerClasses}>
                <ViewColumnsIcon />
                Columns
            </DropdownButton>
            <DropdownMenu anchor="bottom end">
                {columns.map((col) => (
                    <DropdownItem key={col.key} onClick={() => onToggle(col.key)}>
                        <DropdownLabel>{col.label}</DropdownLabel>
                        {visible[col.key] !== false && <CheckIcon data-slot="icon" className="col-start-5" />}
                    </DropdownItem>
                ))}
            </DropdownMenu>
        </Dropdown>
    )
}

export function isColumnVisible(visible: Record<string, boolean>, key: string) {
    return visible[key] !== false
}
